import { User, userInfoWithDefault, Contact } from './user';

type BoolStr = 'TRUE' | 'FALSE';

const toBoolStr = (value: string | undefined): BoolStr =>
  value === 'TRUE' ? 'TRUE' : 'FALSE';

export interface CSVUser {
  userID: string;
  isAdmin: BoolStr;
  identification: string;
  isDead: BoolStr;
  // display values
  sei: string;
  mei: string;
  seiKana: string;
  meiKana: string;
  sex: string;
  kyusei: string;
  kyuseiOpen: BoolStr;
  kyuseiKana: string;
  kyuseiKanaOpen: BoolStr;
  enterYear: string;
  enterUniversityYear: string;
  finalEducation: string;
  finalEducationOpen: BoolStr;
  postNumber: string;
  postNumberOpen: BoolStr;
  address: string;
  addressOpen: BoolStr;
  phoneNumber: string;
  phoneNumberOpen: BoolStr;
  faxNumber: string;
  faxNumberOpen: BoolStr;
  postNumberHome: string;
  postNumberHomeOpen: BoolStr;
  addressHome: string;
  addressHomeOpen: BoolStr;
  phoneNumberHome: string;
  phoneNumberHomeOpen: BoolStr;
  faxNumberHome: string;
  faxNumberHomeOpen: BoolStr;
  contact: Contact;
  email: string;
  emailOpen: BoolStr;
  workplace: string;
  workplaceOpen: BoolStr;
  postNumberWorkplace: string;
  postNumberWorkplaceOpen: BoolStr;
  addressWorkplace: string;
  addressWorkplaceOpen: BoolStr;
  phoneNumberWorkplace: string;
  phoneNumberWorkplaceOpen: BoolStr;
  faxNumberWorkplace: string;
  faxNumberWorkplaceOpen: BoolStr;
  comment: string;
  commentOpen: BoolStr;
  birthday: string;
  birthdayOpen: BoolStr;
  //valid
  emailValid: 'new' | 'invalid';
  currentValid: 'new' | 'invalid';
  homeValid: 'new' | 'invalid';

  //output
  postNumberToSend?: string;
  addressToSend?: string;
  contactPath?: '郵送' | 'Email' | 'none';
  message?: string;
}

export const CSVUserWithDefault = (data: Partial<CSVUser>): CSVUser => {
  return {
    userID: data.userID ?? '',
    isAdmin: toBoolStr(data.isAdmin),
    identification: data.identification ?? '',
    isDead: toBoolStr(data.isDead),
    sei: data.sei ?? '',
    mei: data.mei ?? '',
    seiKana: data.seiKana ?? '',
    meiKana: data.meiKana ?? '',
    sex: data.sex ?? '',
    kyusei: data.kyusei ?? '',
    kyuseiOpen: toBoolStr(data.kyuseiOpen),
    kyuseiKana: data.kyuseiKana ?? '',
    kyuseiKanaOpen: toBoolStr(data.kyuseiKanaOpen),
    enterYear: data.enterYear ?? '',
    enterUniversityYear: data.enterUniversityYear ?? '',
    finalEducation: data.finalEducation ?? '',
    finalEducationOpen: toBoolStr(data.finalEducationOpen),
    postNumber: data.postNumber ?? '',
    postNumberOpen: toBoolStr(data.postNumberOpen),
    address: data.address ?? '',
    addressOpen: toBoolStr(data.addressOpen),
    phoneNumber: data.phoneNumber ?? '',
    phoneNumberOpen: toBoolStr(data.phoneNumberOpen),
    faxNumber: data.faxNumber ?? '',
    faxNumberOpen: toBoolStr(data.faxNumberOpen),
    postNumberHome: data.postNumberHome ?? '',
    postNumberHomeOpen: toBoolStr(data.postNumberHomeOpen),
    addressHome: data.addressHome ?? '',
    addressHomeOpen: toBoolStr(data.addressHomeOpen),
    phoneNumberHome: data.phoneNumberHome ?? '',
    phoneNumberHomeOpen: toBoolStr(data.phoneNumberHomeOpen),
    faxNumberHome: data.faxNumberHome ?? '',
    faxNumberHomeOpen: toBoolStr(data.faxNumberHomeOpen),
    contact: data.contact ?? 'email',
    email: data.email ?? '',
    emailOpen: toBoolStr(data.emailOpen),
    workplace: data.workplace ?? '',
    workplaceOpen: toBoolStr(data.workplaceOpen),
    postNumberWorkplace: data.postNumberWorkplace ?? '',
    postNumberWorkplaceOpen: toBoolStr(data.postNumberWorkplaceOpen),
    addressWorkplace: data.addressWorkplace ?? '',
    addressWorkplaceOpen: toBoolStr(data.addressWorkplaceOpen),
    phoneNumberWorkplace: data.phoneNumberWorkplace ?? '',
    phoneNumberWorkplaceOpen: toBoolStr(data.phoneNumberWorkplaceOpen),
    faxNumberWorkplace: data.faxNumberWorkplace ?? '',
    faxNumberWorkplaceOpen: toBoolStr(data.faxNumberWorkplaceOpen),
    birthday: data.birthday ?? '',
    birthdayOpen: toBoolStr(data.birthdayOpen),
    comment: data.comment ?? '',
    commentOpen: toBoolStr(data.commentOpen),
    emailValid: data.emailValid === 'invalid' ? 'invalid' : 'new',
    currentValid: data.currentValid === 'invalid' ? 'invalid' : 'new',
    homeValid: data.homeValid === 'invalid' ? 'invalid' : 'new',
  };
};

export const UserToCSVUser = (data: User): CSVUser => {
  const _contact = data.contact ?? '';
  const _currentValid = data.currentValid ?? 'invalid';
  const _homeValid = data.homeValid ?? 'invalid';
  const _emailValid = data.emailValid ?? 'invalid';
  const _postNumber = data.postNumber.value ?? '';
  const _address = data.address.value ?? '';
  const _postNumberHome = data.postNumberHome.value ?? '';
  const _addressHome = data.addressHome.value ?? '';
  const _email = data.email.value ?? '';
  let _postNumberToSend;
  let _addressToSend;
  let _contactPath: 'none' | '郵送' | 'Email' = 'none';
  let _message;
  if (_contact === 'current') {
    if (_currentValid === 'new' && _address !== '') {
      _postNumberToSend = _postNumber;
      _addressToSend = _address;
      _contactPath = '郵送';
    } else {
      if (_homeValid === 'new' && _addressHome !== '') {
        _postNumberToSend = _postNumberHome;
        _addressToSend = _addressHome;
        _message =
          '現住所として登録されている住所が無効であったため、帰省先住所にお送りしております。';
        _contactPath = '郵送';
      } else {
        _contactPath = 'none';
        if (_email !== '' && _emailValid === 'new') {
          _message =
            '登録されている住所が無効であったため、メールにてお伝えしております。';
          _contactPath = 'Email';
        }
      }
    }
  } else if (_contact === 'home') {
    if (_homeValid === 'new' && _addressHome !== '') {
      _postNumberToSend = _postNumberHome;
      _addressToSend = _addressHome;
      _contactPath = '郵送';
    } else {
      if (_currentValid === 'new' && _address !== '') {
        _contactPath = '郵送';
        _postNumberToSend = _postNumber;
        _addressToSend = _address;
        _message =
          '帰省先として登録されている住所が無効であったため、現住所にお送りしております。';
      } else {
        _contactPath = 'none';
        if (_email !== '' && _emailValid === 'new') {
          _message =
            '登録されている住所が無効であったため、メールにてお伝えしております。';
          _contactPath = 'Email';
        }
      }
    }
  }
  return {
    userID: data.userID ?? '',
    isAdmin: data.isAdmin ? 'TRUE' : 'FALSE',
    identification: data.identification ?? '',
    isDead: data.isDead ? 'TRUE' : 'FALSE',
    sei: data.sei ?? '',
    mei: data.mei ?? '',
    seiKana: data.seiKana ?? '',
    meiKana: data.meiKana ?? '',
    sex: data.sex ?? '',
    kyusei: data.kyusei.value ?? '',
    kyuseiOpen: data.kyusei.open ? 'TRUE' : 'FALSE',
    kyuseiKana: data.kyuseiKana.value ?? '',
    kyuseiKanaOpen: data.kyuseiKana.open ? 'TRUE' : 'FALSE',
    enterYear: data.enterYear ?? '',
    enterUniversityYear: data.enterUniversityYear ?? '',
    finalEducation: data.finalEducation.value ?? '',
    finalEducationOpen: data.finalEducation.open ? 'TRUE' : 'FALSE',
    postNumber: data.postNumber.value ?? '',
    postNumberOpen: data.postNumber.open ? 'TRUE' : 'FALSE',
    address: data.address.value ?? '',
    addressOpen: data.address.open ? 'TRUE' : 'FALSE',
    phoneNumber: data.phoneNumber.value ?? '',
    phoneNumberOpen: data.phoneNumber.open ? 'TRUE' : 'FALSE',
    faxNumber: data.faxNumber.value ?? '',
    faxNumberOpen: data.faxNumber.open ? 'TRUE' : 'FALSE',
    postNumberHome: data.postNumberHome.value ?? '',
    postNumberHomeOpen: data.postNumberHome.open ? 'TRUE' : 'FALSE',
    addressHome: data.addressHome.value ?? '',
    addressHomeOpen: data.addressHome.open ? 'TRUE' : 'FALSE',
    phoneNumberHome: data.phoneNumberHome.value ?? '',
    phoneNumberHomeOpen: data.phoneNumberHome.open ? 'TRUE' : 'FALSE',
    faxNumberHome: data.faxNumberHome.value ?? '',
    faxNumberHomeOpen: data.faxNumberHome.open ? 'TRUE' : 'FALSE',
    contact: data.contact ?? 'email',
    email: data.email.value ?? '',
    emailOpen: data.email.open ? 'TRUE' : 'FALSE',
    workplace: data.workplace.value ?? '',
    workplaceOpen: data.workplace.open ? 'TRUE' : 'FALSE',
    postNumberWorkplace: data.postNumberWorkplace.value ?? '',
    postNumberWorkplaceOpen: data.postNumberWorkplace.open ? 'TRUE' : 'FALSE',
    addressWorkplace: data.addressWorkplace.value ?? '',
    addressWorkplaceOpen: data.addressWorkplace.open ? 'TRUE' : 'FALSE',
    phoneNumberWorkplace: data.phoneNumberWorkplace.value ?? '',
    phoneNumberWorkplaceOpen: data.phoneNumberWorkplace.open ? 'TRUE' : 'FALSE',
    faxNumberWorkplace: data.faxNumberWorkplace.value ?? '',
    faxNumberWorkplaceOpen: data.faxNumberWorkplace.open ? 'TRUE' : 'FALSE',
    birthday: data.birthday.value ?? '',
    birthdayOpen: data.birthday.open ? 'TRUE' : 'FALSE',
    comment: data.comment.value ?? '',
    commentOpen: data.comment.open ? 'TRUE' : 'FALSE',
    emailValid: data.emailValid,
    currentValid: data.currentValid,
    homeValid: data.homeValid,
    postNumberToSend: _postNumberToSend ?? '',
    addressToSend: _addressToSend ?? '',
    contactPath: _contactPath ?? 'none',
    message: _message ?? '',
  };
};

export const CSVUserToUser = (data: CSVUser): User => {
  return {
    userID: data.userID ?? '',
    isAdmin: data.isAdmin === 'TRUE',
    identification: data.identification ?? '',
    isDead: data.isDead === 'TRUE',
    sei: data.sei ?? '',
    mei: data.mei ?? '',
    seiKana: data.seiKana ?? '',
    meiKana: data.meiKana ?? '',
    sex: data.sex ?? '',
    kyusei: userInfoWithDefault({
      value: data.kyusei ?? '',
      open: data.kyuseiOpen === 'TRUE',
    }),
    kyuseiKana: userInfoWithDefault({
      value: data.kyuseiKana ?? '',
      open: data.kyuseiKanaOpen === 'TRUE',
    }),
    enterYear: data.enterYear ?? '',
    enterUniversityYear: data.enterUniversityYear ?? '',
    finalEducation: userInfoWithDefault({
      value: data.finalEducation ?? '',
      open: data.finalEducationOpen === 'TRUE',
    }),
    postNumber: userInfoWithDefault({
      value: data.postNumber ?? '',
      open: data.postNumberOpen === 'TRUE',
    }),
    address: userInfoWithDefault({
      value: data.address ?? '',
      open: data.address === 'TRUE',
    }),
    phoneNumber: userInfoWithDefault({
      value: data.phoneNumber ?? '',
      open: data.phoneNumber === 'TRUE',
    }),
    faxNumber: userInfoWithDefault({
      value: data.faxNumber ?? '',
      open: data.faxNumberOpen === 'TRUE',
    }),
    postNumberHome: userInfoWithDefault({
      value: data.postNumberHome ?? '',
      open: data.postNumberHomeOpen === 'TRUE',
    }),
    addressHome: userInfoWithDefault({
      value: data.addressHome ?? '',
      open: data.addressHomeOpen === 'TRUE',
    }),
    phoneNumberHome: userInfoWithDefault({
      value: data.phoneNumberHome ?? '',
      open: data.phoneNumberHomeOpen === 'TRUE',
    }),
    faxNumberHome: userInfoWithDefault({
      value: data.faxNumberHome ?? '',
      open: data.faxNumberHomeOpen === 'TRUE',
    }),
    contact: data.contact ?? 'email',
    email: userInfoWithDefault({
      value: data.email ?? '',
      open: data.emailOpen === 'TRUE',
    }),
    workplace: userInfoWithDefault({
      value: data.workplace ?? '',
      open: data.workplaceOpen === 'TRUE',
    }),
    postNumberWorkplace: userInfoWithDefault({
      value: data.postNumberWorkplace ?? '',
      open: data.postNumberWorkplace === 'TRUE',
    }),
    addressWorkplace: userInfoWithDefault({
      value: data.addressWorkplace ?? '',
      open: data.addressWorkplaceOpen === 'TRUE',
    }),
    phoneNumberWorkplace: userInfoWithDefault({
      value: data.phoneNumberWorkplace ?? '',
      open: data.phoneNumberWorkplaceOpen === 'TRUE',
    }),
    faxNumberWorkplace: userInfoWithDefault({
      value: data.faxNumberWorkplace ?? '',
      open: data.faxNumberWorkplaceOpen === 'TRUE',
    }),
    comment: userInfoWithDefault({
      value: data.comment ?? '',
      open: data.commentOpen === 'TRUE',
    }),
    birthday: userInfoWithDefault({
      value: data.birthday ?? '',
      open: data.birthdayOpen === 'TRUE',
    }),
    privilegePeriod: [],
    emailValid: data.emailValid,
    currentValid: data.currentValid,
    homeValid: data.homeValid,
  };
};
